/**
 * G4C CP2 — Quality Score + Export UI copy strings, LOCKED.
 *
 * Every user-facing string in the QualityScoreStep + ExportDropdown
 * is exported from this module. Reason: we run a banned-phrase
 * regression test (`quality_copy.test.js`) that sweeps every string
 * in this module against the same BANNED_PHRASES vocabulary the
 * backend uses (`modules/proposals/prompts/proposal_sections_v1.py::
 * BANNED_PHRASES`). If a future edit accidentally introduces
 * "guaranteed" or "you qualify" or "coming soon", the test fails
 * BEFORE the copy ships.
 *
 * Voice discipline (user G4C CP2 §6, verbatim):
 *   "This score evaluates the current draft against available
 *    requirements and writing-quality signals. It does not predict
 *    an award decision."
 *
 * That disclaimer is EXACT — the snapshot test locks it. Do not
 * edit without an explicit user brief.
 */

export const QUALITY_DISCLAIMER =
  "This score evaluates the current draft against available requirements " +
  "and writing-quality signals. It does not predict an award decision.";

export const QUALITY_STEP_TITLE = "Quality Score";

export const QUALITY_STEP_SUBTITLE =
  "Draft-level guidance across five dimensions. Regenerate individual sections for targeted improvements.";

export const OVERALL_SCORE_LABEL = "Overall proposal score";

export const DIMENSION_LABELS = {
  completeness: "Completeness",
  length: "Length",
  readability: "Readability",
  alignment: "Alignment",
  evidence: "Evidence",
};

export const DIMENSION_EXPLAINERS = {
  completeness:
    "Fraction of the wizard inputs this section relies on that were populated.",
  length:
    "Whether the section falls within the target word-count band for its role.",
  readability:
    "Flesch-Kincaid grade level compared to a professional-audience target.",
  alignment:
    "How well the section addresses what the linked grant asks for.",
  evidence:
    "Whether the section draws on the specific facts you provided in the wizard.",
};

export const KILL_SWITCH_BADGE_LABEL = "Live scoring paused";
export const KILL_SWITCH_EXPLAINER =
  "Live scoring temporarily unavailable — draft-level guidance shown.";

export const LOW_CONFIDENCE_EXPLAINER =
  "AI scoring returned a neutral value for this proposal. Rule-based signals still apply.";

export const WEAK_SECTION_CHIP_LABEL = "Recommended improvement";
export const WEAK_SECTION_CHIP_ARIA =
  "This section scored below the improvement threshold.";
// G4C CP2 polish rerun (2026-07-20) — server-provided threshold is
// rendered as a compact badge on each weak-section row. String is
// export-ed here so it is caught by the banned-phrase sweep in
// `quality_copy.test.js` alongside every other user-facing label.
export const WEAK_SECTION_TARGET_LABEL_PREFIX = "target: ";
export const WEAK_SECTION_TARGET_TITLE_PREFIX = "Target section score: ";

export const RECOMMENDED_IMPROVEMENTS_TITLE = "Recommended improvements";
export const RECOMMENDED_IMPROVEMENTS_EMPTY =
  "No specific improvements surfaced for this draft.";

export const COMPUTE_SCORE_BUTTON = "Compute quality score";
export const RECOMPUTE_SCORE_BUTTON = "Refresh score";

export const EMPTY_STATE_TITLE = "No sections to score yet";
export const EMPTY_STATE_BODY =
  "Complete step 9 to generate sections first, then return here to see quality signals.";
export const EMPTY_STATE_CTA = "Go to step 9";

export const RETRY_BUTTON = "Retry";
export const RETRY_BANNER_TITLE = "Could not load the quality score";

// Export dropdown
export const EXPORT_TRIGGER_LABEL = "Export";
export const EXPORT_FORMAT_LABELS = {
  pdf: "Download as PDF",
  docx: "Download as DOCX",
  txt: "Download as TXT",
};
export const EXPORT_NO_SECTIONS_TITLE = "Nothing to export yet";
export const EXPORT_NO_SECTIONS_BODY =
  "This proposal has no exportable content yet. Generate sections before exporting.";
export const EXPORT_FAILURE_BANNER_TITLE = "Download did not complete";
export const EXPORT_FAILURE_BANNER_BODY_FALLBACK =
  "Please try the download again. If the issue persists, contact support.";

// Free-tier + feature-disabled state
export const FREE_TIER_QUALITY_TITLE = "Quality scoring is a Premium feature";
export const FREE_TIER_QUALITY_BODY =
  "Upgrade to Premium to see the 5-dimension quality score, weak-section flags, and recommended improvements.";
export const FREE_TIER_EXPORT_TITLE = "Export is a Premium feature";
export const FREE_TIER_EXPORT_BODY =
  "Upgrade to Premium to download PDF, DOCX, or TXT copies of this proposal.";
export const UPGRADE_CTA_LABEL = "Upgrade to Premium";

export const FEATURE_DISABLED_TITLE = "Grant Writer is temporarily disabled";
export const FEATURE_DISABLED_BODY =
  "An administrator has paused the Grant Writer module. Your saved proposal drafts remain in the system and will re-appear when the feature is re-enabled.";
